'use client'

import { useState, useCallback, useMemo, useEffect } from 'react'
import { useBirhaus } from '@birhaus/provider'
import { checkSEPRELADThreshold } from '../utils/currency'
import type { 
  Transaction, 
  TransactionType, 
  TransactionStatus,
  TransactionFilters,
  CreateTransactionData,
  UseTransactionsReturn,
  CurrencyCode
} from '../types/financial'

/**
 * useTransactions - Hook for transaction operations and state management
 * 
 * Features:
 * - Complete transaction CRUD operations
 * - Real-time filtering and search capabilities
 * - SEPRELAD compliance checking and alerts
 * - Transaction categorization and analytics
 * - Spanish-first error messages and validation
 * - Optimistic updates for better UX
 * - Error handling and retry logic
 * - Export functionality for transaction reports
 * - Transaction status tracking and updates
 */
export function useTransactions(accountId?: string): UseTransactionsReturn {
  const { language, reportViolation } = useBirhaus()
  
  // Transaction state
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<TransactionFilters>({})

  // Load transactions on mount or account change
  useEffect(() => {
    if (accountId) {
      loadTransactions()
    }
  }, [accountId])

  // Load transactions from API/storage
  const loadTransactions = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Simulate API call - in real implementation, fetch from Supabase
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Mock transaction data for development
      const mockTransactions: Transaction[] = [
        {
          id: '1',
          tipo: 'ingreso',
          monto: 500000,
          moneda: 'PYG',
          categoria: 'salario',
          descripcionEs: 'Transferencia de Juan Pérez',
          fecha: new Date('2024-01-15T10:30:00'),
          estado: 'completada',
          referencia: 'TRF001',
          cuentaOrigen: 'Juan Pérez',
          comision: 0
        },
        {
          id: '2',
          tipo: 'gasto',
          monto: 150000,
          moneda: 'PYG',
          categoria: 'servicios',
          descripcionEs: 'Pago ANDE - Electricidad',
          fecha: new Date('2024-01-14T14:20:00'),
          estado: 'completada',
          referencia: 'ANDE2024001',
          cuentaDestino: 'ANDE',
          comision: 5000
        }
      ]
      
      setTransactions(mockTransactions)
    } catch (err) {
      const errorMessage = language === 'es' 
        ? 'Error al cargar transacciones'
        : 'Error loading transactions'
      setError(errorMessage)
      console.error('Error loading transactions:', err)
    } finally {
      setLoading(false)
    }
  }, [accountId, language])

  // Create new transaction
  const createTransaction = useCallback(async (data: CreateTransactionData) => {
    setLoading(true)
    setError(null)

    try {
      // SEPRELAD compliance check
      const seprelad = checkSEPRELADThreshold(data.monto, data.moneda)
      if (seprelad.requiresReport) {
        reportViolation({
          type: 'performance',
          severity: 'warning',
          element: document.createElement('div'),
          message: `Transaction amount ${seprelad.amountUSD} USD exceeds SEPRELAD threshold`,
          messageEs: `Monto de transacción ${seprelad.amountUSD} USD supera umbral SEPRELAD`,
          messageEn: `Transaction amount ${seprelad.amountUSD} USD exceeds SEPRELAD threshold`,
          recommendation: 'Transaction will be flagged for compliance reporting',
          birhausPrinciple: 8 // Compliance principle
        })
      }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))

      const newTransaction: Transaction = {
        id: Date.now().toString(),
        ...data,
        fecha: new Date(),
        estado: 'pendiente'
      }

      // Optimistic update
      setTransactions(prev => [newTransaction, ...prev])

      // Simulate status update after processing
      setTimeout(() => {
        setTransactions(prev => prev.map(t => 
          t.id === newTransaction.id 
            ? { ...t, estado: 'completada' }
            : t
        ))
      }, 2000)

      return newTransaction
    } catch (err) {
      const errorMessage = language === 'es' 
        ? 'Error al crear transacción'
        : 'Error creating transaction'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [language, reportViolation])

  // Update transaction status
  const updateTransactionStatus = useCallback(async (
    transactionId: string, 
    status: TransactionStatus
  ) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 300))

      setTransactions(prev => prev.map(transaction =>
        transaction.id === transactionId
          ? { ...transaction, estado: status }
          : transaction
      ))
    } catch (err) {
      const errorMessage = language === 'es' 
        ? 'Error al actualizar estado de transacción'
        : 'Error updating transaction status'
      setError(errorMessage)
      throw err
    }
  }, [language])

  // Cancel transaction
  const cancelTransaction = useCallback(async (transactionId: string) => {
    try {
      await updateTransactionStatus(transactionId, 'cancelada')
    } catch (err) {
      console.error('Error cancelling transaction:', err)
      throw err
    }
  }, [updateTransactionStatus])

  // Retry failed transaction
  const retryTransaction = useCallback(async (transactionId: string) => {
    try {
      await updateTransactionStatus(transactionId, 'procesando')
      
      // Simulate processing delay
      setTimeout(async () => {
        try {
          await updateTransactionStatus(transactionId, 'completada')
        } catch (err) {
          await updateTransactionStatus(transactionId, 'fallida')
        }
      }, 3000)
    } catch (err) {
      console.error('Error retrying transaction:', err)
      throw err
    }
  }, [updateTransactionStatus])

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    let filtered = transactions

    // Type filter
    if (filters.tipo?.length) {
      filtered = filtered.filter(t => filters.tipo!.includes(t.tipo))
    }

    // Status filter  
    if (filters.estado?.length) {
      filtered = filtered.filter(t => filters.estado!.includes(t.estado))
    }

    // Date range filter
    if (filters.fechaDesde) {
      filtered = filtered.filter(t => new Date(t.fecha) >= new Date(filters.fechaDesde!))
    }
    if (filters.fechaHasta) {
      filtered = filtered.filter(t => new Date(t.fecha) <= new Date(filters.fechaHasta!))
    }

    // Amount range filter
    if (filters.montoMinimo !== undefined) {
      filtered = filtered.filter(t => t.monto >= filters.montoMinimo!)
    }
    if (filters.montoMaximo !== undefined) {
      filtered = filtered.filter(t => t.monto <= filters.montoMaximo!)
    }

    // Currency filter
    if (filters.moneda?.length) {
      filtered = filtered.filter(t => filters.moneda!.includes(t.moneda))
    }

    return filtered.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
  }, [transactions, filters])

  // Transaction analytics
  const analytics = useMemo(() => {
    const totalIncoming = filteredTransactions
      .filter(t => ['transferencia_entrante', 'deposito'].includes(t.tipo) && t.estado === 'completada')
      .reduce((sum, t) => sum + t.monto, 0)

    const totalOutgoing = filteredTransactions
      .filter(t => ['transferencia_saliente', 'retiro', 'pago_servicios', 'compra'].includes(t.tipo) && t.estado === 'completada')
      .reduce((sum, t) => sum + t.monto, 0)

    const totalCommissions = filteredTransactions
      .filter(t => t.estado === 'completada')
      .reduce((sum, t) => sum + (t.comision || 0), 0)

    const pendingTransactions = filteredTransactions.filter(t => 
      ['pendiente', 'procesando'].includes(t.estado)
    ).length

    const failedTransactions = filteredTransactions.filter(t => 
      t.estado === 'fallida'
    ).length

    // Group by transaction type
    const byType = filteredTransactions.reduce((acc, transaction) => {
      if (!acc[transaction.tipo]) {
        acc[transaction.tipo] = {
          count: 0,
          totalAmount: 0
        }
      }
      acc[transaction.tipo].count++
      acc[transaction.tipo].totalAmount += transaction.monto
      return acc
    }, {} as Record<TransactionType, { count: number; totalAmount: number }>)

    return {
      totalIncoming,
      totalOutgoing,
      totalCommissions,
      netFlow: totalIncoming - totalOutgoing,
      pendingTransactions,
      failedTransactions,
      byType
    }
  }, [filteredTransactions])

  // Export transactions to CSV
  const exportTransactions = useCallback((exportTransactions?: Transaction[]) => {
    const dataToExport = exportTransactions || filteredTransactions
    
    const headers = language === 'es' 
      ? ['Fecha', 'Tipo', 'Descripción', 'Monto', 'Moneda', 'Estado', 'Referencia', 'Comisión']
      : ['Date', 'Type', 'Description', 'Amount', 'Currency', 'Status', 'Reference', 'Commission']

    const rows = dataToExport.map(transaction => [
      transaction.fecha.toISOString().split('T')[0],
      transaction.tipo,
      transaction.descripcionEs,
      transaction.monto.toString(),
      transaction.moneda,
      transaction.estado,
      transaction.referencia || '',
      (transaction.comision || 0).toString()
    ])

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `transacciones_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [filteredTransactions, language])

  // Get transaction by ID
  const getTransactionById = useCallback((id: string): Transaction | undefined => {
    return transactions.find(t => t.id === id)
  }, [transactions])

  // Check if transaction requires SEPRELAD reporting
  const checkTransactionCompliance = useCallback((transaction: Transaction) => {
    return checkSEPRELADThreshold(transaction.monto, transaction.moneda)
  }, [])

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<TransactionFilters>) => {
    setFilters((prev: TransactionFilters) => ({ ...prev, ...newFilters }))
  }, [])

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({})
  }, [])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Refresh transactions
  const refresh = useCallback(() => {
    if (accountId) {
      loadTransactions()
    }
  }, [loadTransactions, accountId])

  // Get transactions by type
  const getTransactionsByType = useCallback((type: TransactionType) => {
    return transactions.filter(t => t.tipo === type)
  }, [transactions])

  // Get transactions by status
  const getTransactionsByStatus = useCallback((status: TransactionStatus) => {
    return transactions.filter(t => t.estado === status)
  }, [transactions])

  // Get transactions by date range
  const getTransactionsByDateRange = useCallback((startDate: Date, endDate: Date) => {
    return transactions.filter(t => {
      const transactionDate = new Date(t.fecha)
      return transactionDate >= startDate && transactionDate <= endDate
    })
  }, [transactions])

  // Get recent transactions
  const getRecentTransactions = useCallback((limit: number = 10) => {
    return transactions
      .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
      .slice(0, limit)
  }, [transactions])

  return {
    transactions,
    filteredTransactions,
    filters,
    setFilters: updateFilters,
    addTransaction: createTransaction,
    updateTransaction: (id: string, updates: Partial<Transaction>) => {
      setTransactions(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t))
    },
    deleteTransaction: (id: string) => {
      setTransactions(prev => prev.filter(t => t.id !== id))
    },
    calculateSummary: () => ({
      ingresosMensuales: 0,
      gastosMensuales: 0,
      ahorrosMensuales: 0,
      porcentajeAhorros: 0,
      proyeccionAnual: 0
    })
  }
}

export type { UseTransactionsReturn }